import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Layout, Button, Input, Segmented, Select, Spin, message, Popconfirm, Tooltip, Empty } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  FileTextTwoTone,
  DeleteTwoTone,
  SafetyCertificateTwoTone,
  ArrowRightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import NavigationBar from '../../components/NavigationBar';
import DOTheme from '../../components/directOrders/doTheme';
import ticketService from '../../services/ticketService';
import { useAuth } from '../../context/AuthContext';
import { isTicketAdmin } from '../../utils/ticketAdmin';
import { approverAt, firstName, useApprovers } from '../../utils/approverStore';
import {
  fieldLabel,
  getApprovalStatus,
  getNextApprover,
  getTicketSteps,
  canUserApprove,
  isApproverEmail,
  ticketStatus,
} from '../../utils/ticketFields';
import '../../styles/support.css';

const { TextArea } = Input;

const fmtValue = (v) => (v === undefined || v === null || v === '' ? '—' : String(v));

/**
 * Review queue for direct order tickets: the list on the left, the selected ticket on the right
 * with its approval chain, the changes it asks for, and the approve/reject decision.
 */
const SupportTickets = () => {
  const { user } = useAuth();
  const userEmail = user?.email;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: 'open' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminComment, setAdminComment] = useState('');
  // Re-renders once the approval chain has loaded, so access and names are right.
  const { loaded: approversLoaded } = useApprovers();

  const isApprover = isApproverEmail(userEmail);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ticketService.listTickets(filters);
      if (response.success) {
        setTickets(response.tickets);
      } else {
        message.error(response.error || 'Failed to fetch tickets');
      }
    } catch (error) {
      message.error('An error occurred while fetching tickets');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const viewTicketDetails = async (ticketId) => {
    setSelectedId(ticketId);
    setAdminComment('');
    setDetailLoading(true);
    try {
      const response = await ticketService.getTicket(ticketId);
      if (response.success) {
        setSelectedTicket(response.ticket);
      } else {
        message.error('Failed to load ticket details');
      }
    } catch (error) {
      message.error('An error occurred');
    } finally {
      setDetailLoading(false);
    }
  };

  // Open the first ticket in the list when nothing is selected (or the selection left the list)
  useEffect(() => {
    if (loading) return;
    if (tickets.length === 0) {
      setSelectedId(null);
      setSelectedTicket(null);
      return;
    }
    if (!selectedId || !tickets.some((t) => t.ticket_id === selectedId)) {
      viewTicketDetails(tickets[0].ticket_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, loading]);

  const afterDecision = () => {
    setSelectedTicket(null);
    setSelectedId(null);
    setAdminComment('');
    fetchTickets();
  };

  const handleApprove = async () => {
    if (!selectedTicket || !isApprover) return;

    setActionLoading(true);
    try {
      const response = await ticketService.approveTicket(
        selectedTicket.ticket_id,
        userEmail,
        adminComment
      );

      if (response.success) {
        message.success(response.message);
        afterDecision();
      } else {
        message.error(response.error || 'Failed to approve ticket');
      }
    } catch (error) {
      message.error('An error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTicket || !isApprover) return;

    setActionLoading(true);
    try {
      const response = await ticketService.rejectTicket(
        selectedTicket.ticket_id,
        userEmail,
        adminComment
      );

      if (response.success) {
        message.success(response.message);
        afterDecision();
      } else {
        message.error(response.error || 'Failed to reject ticket');
      }
    } catch (error) {
      message.error('An error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  if (!approversLoaded) return null;
  if (user && !isTicketAdmin(userEmail)) return <Navigate to="/dashboard" replace />;

  const commentMissing = !adminComment || adminComment.trim() === '';

  const STEP_CLASS = { approved: 'is-done', rejected: 'is-bad', current: 'is-now', waiting: '' };

  const renderApprovalTrack = (t) => {
    const steps = getTicketSteps(t);
    const isDelete = t.ticket_type === 'delete';
    const stepLabel = (s) => {
      if (s.state === 'approved') return 'Approved';
      if (s.state === 'rejected') return 'Rejected';
      if (s.state === 'current') {
        if (s.email && s.email === userEmail?.toLowerCase()) return 'Your turn';
        return isDelete ? 'One approval deletes the order' : 'Their turn';
      }
      return t.status === 'open' ? 'Waiting' : '—';
    };
    return (
      <ol className={`tk-track${isDelete ? ' is-single' : ''}`}>
        {steps.map((s) => (
          <li key={s.key} className={`tk-track__step ${STEP_CLASS[s.state] || ''}`}>
            <i aria-hidden="true" />
            <b title={s.title ? `${s.name}, ${s.title}` : s.name}>{s.name}</b>
            <span>{stepLabel(s)}</span>
          </li>
        ))}
      </ol>
    );
  };

  const renderDetail = () => {
    if (detailLoading && !selectedTicket) return <div className="tk-detail__state"><Spin /></div>;
    if (!selectedTicket) {
      return (
        <div className="tk-detail__state">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tickets here. Pick another filter to see more." />
        </div>
      );
    }
    const t = selectedTicket;
    const st = ticketStatus(t.status);
    const changes = t.ticket_type === 'amend' && t.proposed_changes ? Object.entries(t.proposed_changes) : [];
    const canDecide = canUserApprove(t, userEmail);

    return (
      <Spin spinning={detailLoading}>
        <header className="tk-detail__head">
          <span className={`tk-detail__icon tone-${t.ticket_type === 'delete' ? 'bad' : 'accent'}`} aria-hidden="true">
            {t.ticket_type === 'delete' ? <DeleteTwoTone twoToneColor="#c4321f" /> : <FileTextTwoTone twoToneColor="#2748d9" />}
          </span>
          <div className="tk-detail__titles">
            <h2 className="tk-detail__title">
              #{t.ticket_id} {t.ticket_type === 'delete' ? 'Delete' : 'Change'} order #{t.doRef || t.currentDoRef}
            </h2>
            <p className="tk-detail__meta">
              {[t.requesterName, t.created_at ? `sent ${dayjs(t.created_at).format('D MMM YYYY, HH:mm')}` : null, t.doSupplier].filter(Boolean).join(' · ')}
            </p>
          </div>
          <span className={`tk-status tone-${st.tone}`}>{st.label}</span>
        </header>

        <section className="tk-sec">
          <h3 className="tk-sec__title">Approvals</h3>
          {renderApprovalTrack(t)}
        </section>

        {changes.length > 0 && (
          <section className="tk-sec">
            <h3 className="tk-sec__title">Requested changes</h3>
            <div className="tk-diff">
              {changes.map(([key, value]) => (
                <div key={key} className="tk-diff__row">
                  <span className="tk-diff__label">{fieldLabel(key)}</span>
                  <span className="tk-diff__old">{fmtValue(t.original_snapshot?.[key])}</span>
                  <ArrowRightOutlined className="tk-diff__arrow" aria-hidden="true" />
                  <span className="tk-diff__new">{fmtValue(value)}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {t.ticket_type === 'delete' && t.status === 'open' && (
          <p className="tk-callout tone-bad">This direct order will be deleted once it's approved. Only the first approval is needed{approverAt(1) ? ` (${firstName(approverAt(1))})` : ''}.</p>
        )}

        <section className="tk-sec">
          <h3 className="tk-sec__title">Reason</h3>
          <p className="tk-reason">{t.reason || 'No reason provided'}</p>
          {t.admin_comment && (
            <p className="tk-reason is-comment"><b>Reviewer's comment</b>{t.admin_comment}</p>
          )}
        </section>

        {isApprover && t.status === 'open' && (
          <section className="tk-sec tk-decide">
            <h3 className="tk-sec__title">Your decision</h3>
            {canDecide ? (
              <>
                <label className="tk-decide__label" htmlFor="tk-comment">
                  Comment <span>(optional to approve, required to reject; the requester sees it)</span>
                </label>
                <TextArea
                  id="tk-comment"
                  rows={3}
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                  placeholder="Add a note for the requester"
                />
                <div className="tk-decide__actions">
                  <Tooltip title={commentMissing ? 'Add a reason in the comment box to reject' : ''}>
                    <Popconfirm
                      title="Reject this ticket?"
                      description="The ticket closes straight away and the requester sees your comment."
                      onConfirm={handleReject}
                      okText="Reject"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancel"
                      disabled={commentMissing}
                    >
                      <Button
                        danger
                        icon={<CloseOutlined />}
                        loading={actionLoading}
                        disabled={commentMissing}
                        onClick={() => {
                          if (commentMissing) {
                            message.error('A rejection reason is required. Please add a comment explaining why this ticket is being rejected.', 5);
                          }
                        }}
                      >
                        Reject
                      </Button>
                    </Popconfirm>
                  </Tooltip>
                  <Popconfirm
                    title="Approve this ticket?"
                    description="This can't be undone."
                    onConfirm={handleApprove}
                    okText="Approve"
                    cancelText="Cancel"
                  >
                    <Button type="primary" icon={<CheckOutlined />} loading={actionLoading}>
                      Approve
                    </Button>
                  </Popconfirm>
                </div>
              </>
            ) : (
              <p className="tk-callout">
                {t.ticket_type === 'delete'
                  ? `Delete tickets only need the first approval. Waiting for ${getNextApprover(t)}.`
                  : `Not your turn yet. Waiting for ${getNextApprover(t)}.`}
              </p>
            )}
          </section>
        )}
      </Spin>
    );
  };

  return (
    <DOTheme>
      <Layout className="do-shell do-scope tk-page">
        <NavigationBar />
        <main className="do-main tk-main">
          <aside className="tk-queue" aria-label="Tickets">
            <div className="tk-queue__head">
              <span className="tk-queue__icon" aria-hidden="true"><SafetyCertificateTwoTone twoToneColor="#6941c6" /></span>
              <div>
                <h1 className="tk-queue__title">Tickets</h1>
                <p className="tk-queue__sub">Direct order change and delete requests.</p>
              </div>
            </div>
            <div className="tk-queue__filters">
              <Segmented
                block
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
                options={[
                  { value: 'open', label: 'To review' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' },
                  { value: '', label: 'All' },
                ]}
                aria-label="Status"
              />
              <div className="tk-queue__row">
                <Select
                  value={filters.type}
                  onChange={(value) => setFilters({ ...filters, type: value })}
                  placeholder="All types"
                  allowClear
                  style={{ flex: 1 }}
                  options={[{ value: 'amend', label: 'Changes' }, { value: 'delete', label: 'Deletes' }]}
                  aria-label="Type"
                />
                <Button icon={<ReloadOutlined />} onClick={fetchTickets} aria-label="Refresh" />
              </div>
            </div>

            {loading && tickets.length === 0 ? (
              <div className="tk-queue__state"><Spin /></div>
            ) : tickets.length === 0 ? (
              <p className="tk-queue__state">No tickets match.</p>
            ) : (
              <ul className="tk-queue__list">
                {tickets.map((t) => {
                  const { approvedCount, totalRequired } = getApprovalStatus(t);
                  return (
                    <li key={t.ticket_id}>
                      <button
                        type="button"
                        className={`tk-qitem${selectedId === t.ticket_id ? ' is-on' : ''}`}
                        onClick={() => viewTicketDetails(t.ticket_id)}
                        aria-current={selectedId === t.ticket_id ? 'true' : undefined}
                      >
                        <span className={`tk-qitem__type tone-${t.ticket_type === 'delete' ? 'bad' : 'accent'}`}>
                          {t.ticket_type === 'delete' ? 'Delete' : 'Change'}
                        </span>
                        <span className="tk-qitem__name">#{t.ticket_id} · order #{t.doRef}</span>
                        <span className="tk-qitem__meta">
                          {t.requesterName || t.created_by_email} · {dayjs(t.created_at).format('D MMM')}
                        </span>
                        <span className="tk-qitem__aps" aria-label={`${approvedCount} of ${totalRequired} approvals`}>
                          {Array.from({ length: totalRequired }, (_, i) => (
                            <i key={i} className={i < approvedCount ? 'is-done' : ''} />
                          ))}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          <section className="tk-detail" aria-live="polite">
            {renderDetail()}
          </section>
        </main>
      </Layout>
    </DOTheme>
  );
};

export default SupportTickets;
